/**
 * FirstPlayDistribution - Goldfish first-play turn spread from a batch run
 *
 * Answers "what turn does this deck actually do something?" across the whole
 * sample, with the share of hands that stalled out inside the same window.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { BatchResults } from '@/features/decks/lib/handSim';
import { BATCH_SIM_TURNS } from '@/features/decks/lib/handSim';

// ============================================================================
// Types
// ============================================================================

export interface FirstPlayDistributionProps {
  results: BatchResults;
}

// ============================================================================
// Component
// ============================================================================

export default function FirstPlayDistribution({ results }: FirstPlayDistributionProps): ReactElement | null {
  const { firstPlayTurnDistribution, totalHands } = results;
  if (totalHands === 0) return null;

  const tallest = Math.max(...firstPlayTurnDistribution);
  const stalledPct = (results.stalledByTurn5 / totalHands) * 100;

  // Index 0 is the "never got going" bucket — show it last, where it reads as the tail.
  const rows = [
    ...firstPlayTurnDistribution.slice(1).map((count, index) => ({ label: `T${index + 1}`, count, muted: false })),
    { label: 'none', count: firstPlayTurnDistribution[0], muted: true },
  ];

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-ash text-[11px] font-medium">First play (goldfish, {BATCH_SIM_TURNS} turns)</span>
        <span className={clsx('text-[11px]', stalledPct >= 20 ? 'text-amber-400' : 'text-ash')}>{stalledPct.toFixed(0)}% stalled</span>
      </div>

      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li key={row.label} className="flex items-center gap-2">
            <span className="text-ash w-6 shrink-0 text-[10px] tabular-nums">{row.label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-800">
              <div
                className={clsx('h-full rounded-full', row.muted ? 'bg-rose-500/70' : 'bg-indigo-500/70')}
                style={{ width: tallest > 0 ? `${(row.count / tallest) * 100}%` : '0%' }}
              />
            </div>
            <span className="text-ash w-9 shrink-0 text-right text-[10px] tabular-nums">
              {((row.count / totalHands) * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
